import { journalNotePlainPreview } from '@/lib/journal/journalReadContent';

export const JOURNAL_WRITE_SOURCES = ['step', 'appointment', 'journal', 'day'] as const;
export type JournalWriteSource = (typeof JOURNAL_WRITE_SOURCES)[number];

export const JOURNAL_WRITE_HISTORY_SCOPES = ['course', 'all'] as const;
export type JournalWriteHistoryScope = (typeof JOURNAL_WRITE_HISTORY_SCOPES)[number];

export type JournalWriteParams = {
  appointmentId?: string | null;
  entryId?: string | null;
  courseId?: string | null;
  stepId?: string | null;
  amend?: boolean;
  source?: JournalWriteSource | null;
  history?: JournalWriteHistoryScope | null;
  from?: string | null;
};

function isWriteSource(value: string | null): value is JournalWriteSource {
  return value === 'step' || value === 'appointment' || value === 'journal' || value === 'day';
}

function isHistoryScope(value: string | null): value is JournalWriteHistoryScope {
  return value === 'course' || value === 'all';
}

/** Only same-origin staff paths. Rejects protocol-relative and external URLs. */
export function safeReturnPath(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed.startsWith('/')) return null;
  if (trimmed.startsWith('//')) return null;
  if (trimmed.includes('://')) return null;
  return trimmed;
}

export function parseJournalWriteSearch(search: URLSearchParams): JournalWriteParams {
  const source = search.get('source');
  const history = search.get('history');
  return {
    appointmentId: search.get('appointment'),
    entryId: search.get('entry'),
    courseId: search.get('course'),
    stepId: search.get('step'),
    amend: search.get('amend') === '1',
    source: isWriteSource(source) ? source : null,
    history: isHistoryScope(history) ? history : null,
    from: safeReturnPath(search.get('from')),
  };
}

export function patientJournalWritePath(patientId: string, params: JournalWriteParams = {}): string {
  const search = new URLSearchParams();
  if (params.appointmentId) search.set('appointment', params.appointmentId);
  if (params.entryId) search.set('entry', params.entryId);
  if (params.courseId) search.set('course', params.courseId);
  if (params.stepId) search.set('step', params.stepId);
  if (params.amend) search.set('amend', '1');
  if (params.source) search.set('source', params.source);
  if (params.history) search.set('history', params.history);
  const from = safeReturnPath(params.from);
  if (from) search.set('from', from);
  const query = search.toString();
  return `/patients/${patientId}/journal/skriv${query ? `?${query}` : ''}`;
}

export function journalWriteClosePath(patientId: string, params: JournalWriteParams): string {
  const from = safeReturnPath(params.from);
  if (from) return from;
  const source = params.source ?? 'journal';
  switch (source) {
    case 'day':
    case 'appointment':
      return '/appointments';
    case 'step': {
      const search = new URLSearchParams();
      search.set('tab', 'treatment_courses');
      if (params.courseId) search.set('course', params.courseId);
      if (params.stepId) search.set('step', params.stepId);
      return `/patients/${patientId}?${search}`;
    }
    case 'journal':
      return `/patients/${patientId}?tab=journal`;
    default: {
      const unexpected: never = source;
      return unexpected;
    }
  }
}

export function patientInitials(firstName: string, lastName: string): string {
  const first = firstName.trim().charAt(0);
  const last = lastName.trim().charAt(0);
  return `${first}${last}`.toUpperCase() || '?';
}

export function journalNotePreview(contentMd: string, max = 180): string {
  return journalNotePlainPreview(contentMd, max);
}

export function appointmentDurationMinutes(startsAt: string, endsAt: string): number {
  const ms = new Date(endsAt).getTime() - new Date(startsAt).getTime();
  if (!Number.isFinite(ms) || ms <= 0) return 0;
  return Math.max(1, Math.round(ms / 60000));
}

export function courseAppointmentIdSet(
  steps: ReadonlyArray<{ appointment_id?: string | null }>,
): Set<string> {
  return new Set(steps.map((step) => step.appointment_id).filter((id): id is string => Boolean(id)));
}

export function appointmentJournalWritePath(
  appointment: {
    id: string;
    patient_id?: string | null;
    treatment_course_id?: string | null;
    treatment_course_step_id?: string | null;
  },
  extras?: Pick<JournalWriteParams, 'entryId' | 'from' | 'source' | 'amend'>,
): string | null {
  if (!appointment.patient_id) return null;
  return patientJournalWritePath(appointment.patient_id, {
    appointmentId: appointment.id,
    courseId: appointment.treatment_course_id,
    stepId: appointment.treatment_course_step_id,
    entryId: extras?.entryId,
    amend: extras?.amend,
    source: extras?.source ?? 'appointment',
    from: extras?.from ?? '/appointments',
  });
}
